import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api, { getClients } from '../services/api';

const STATUSES = ['Draft','In Progress','Review','Completed','Cancelled'];
const STATUS_BADGE = { Draft:'badge-muted', 'In Progress':'badge-info', Review:'badge-warn', Completed:'badge-green', Cancelled:'badge-danger' };
const EMPTY = { client_id:'', contract_no:'', scope_of_work:'', business_days:'', deadline:'', assigned_to:'', technical_manager:'', project_manager:'', status:'Draft', notes:'' };

function printJobOrder(jo) {
  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head><title>Job Order - ${jo.jo_number}</title>
  <style>
    body{font-family:Arial,sans-serif;padding:40px;color:#111}
    .header{display:flex;justify-content:space-between;border-bottom:3px solid #00b368;padding-bottom:20px;margin-bottom:30px}
    .company{font-size:26px;font-weight:700;color:#00b368}
    .jo-title{font-size:22px;font-weight:700;text-align:right}
    .jo-number{font-size:28px;font-weight:900;color:#00b368;text-align:right}
    .section{margin-bottom:24px}
    .section-title{font-size:11px;font-weight:700;color:#00b368;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #e0e0e0;padding-bottom:6px;margin-bottom:12px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
    .field{margin-bottom:8px}
    .field-label{font-size:10px;color:#888;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px}
    .field-value{font-size:14px;font-weight:500;color:#111}
    .scope-box{background:#f9f9f9;border:1px solid #e0e0e0;border-radius:8px;padding:16px;font-size:13px;line-height:1.7;min-height:80px}
    .sig-section{display:grid;grid-template-columns:1fr 1fr 1fr;gap:30px;margin-top:40px}
    .sig-box{text-align:center}
    .sig-line{border-top:1px solid #333;margin-top:50px;padding-top:8px;font-size:12px;color:#555}
    .footer{margin-top:40px;text-align:center;font-size:11px;color:#aaa;border-top:1px solid #eee;padding-top:16px}
    .badge{display:inline-block;padding:3px 12px;border-radius:20px;font-size:11px;font-weight:600;background:#dcfce7;color:#166534}
  </style></head><body>
  <div class="header">
    <div>
      <img src="https://teknulugy.online/logo.svg" style="height:38px;width:auto" alt="Teknulugy"/>
      <div style="font-size:12px;color:#666;margin-top:4px">IT Services - Kuwait</div>
      <div style="font-size:11px;color:#888;margin-top:2px">Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D</div>
      <div style="font-size:11px;color:#888">+965 55107000 | info@teknulugy.com</div>
    </div>
    <div>
      <div class="jo-title">JOB ORDER</div>
      <div class="jo-number">JO #${jo.jo_number}</div>
      <div style="text-align:right;font-size:12px;color:#666;margin-top:4px">Date: ${new Date().toLocaleDateString()}</div>
      <div style="text-align:right;margin-top:6px"><span class="badge">${jo.status}</span></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Client Information</div>
    <div class="grid">
      <div class="field"><div class="field-label">Client Name</div><div class="field-value">${jo.client?.name||'--'}</div></div>
      <div class="field"><div class="field-label">Company</div><div class="field-value">${jo.client?.company||'--'}</div></div>
      <div class="field"><div class="field-label">Phone</div><div class="field-value">${jo.client?.phone||'--'}</div></div>
      <div class="field"><div class="field-label">Email</div><div class="field-value">${jo.client?.email||'--'}</div></div>
      <div class="field"><div class="field-label">Civil ID</div><div class="field-value">${jo.client?.civil_id||'--'}</div></div>
      <div class="field"><div class="field-label">Address</div><div class="field-value">${jo.client?.address||'--'}</div></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Job Order Details</div>
    <div class="grid">
      <div class="field"><div class="field-label">Contract No</div><div class="field-value">${jo.contract_no||'--'}</div></div>
      <div class="field"><div class="field-label">Business Days</div><div class="field-value">${jo.business_days?jo.business_days+' days':'--'}</div></div>
      <div class="field"><div class="field-label">Deadline</div><div class="field-value">${jo.deadline||'--'}</div></div>
      <div class="field"><div class="field-label">Assigned To</div><div class="field-value">${jo.assigned_to||'--'}</div></div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Scope of Work</div>
    <div class="scope-box">${jo.scope_of_work||'--'}</div>
  </div>

  ${jo.notes?`<div class="section"><div class="section-title">Notes</div><div class="scope-box">${jo.notes}</div></div>`:''}

  <div class="sig-section">
    <div class="sig-box">
      <div class="sig-line">Client Signature<br/><strong>${jo.client?.name||''}</strong></div>
    </div>
    <div class="sig-box">
      <div class="sig-line">Technical Manager<br/><strong>${jo.technical_manager||''}</strong></div>
    </div>
    <div class="sig-box">
      <div class="sig-line">Project Manager<br/><strong>${jo.project_manager||''}</strong></div>
    </div>
  </div>

  <div class="footer">Teknulugy IT Services — Ahmed Al Jaber Street, Baitak Tower, Floor 1, Office D — +965 55107000 — info@teknulugy.com</div>
  </body></html>`);
  win.document.close();
  setTimeout(()=>win.print(), 500);
}

export default function JobOrdersPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('All');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [selectedClient, setSelectedClient] = useState(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  const { data:joData, isLoading } = useQuery({
    queryKey:['job-orders', statusFilter],
    queryFn:()=>api.get('/job-orders',{params:{status:statusFilter==='All'?undefined:statusFilter}}).then(r=>r.data),
  });
  const { data:clientData } = useQuery({
    queryKey:['clients-list'],
    queryFn:()=>getClients({limit:200}).then(r=>r.data),
  });

  const createMut = useMutation({
    mutationFn: data=>api.post('/job-orders', data),
    onSuccess:()=>{ qc.invalidateQueries(['job-orders']); setShowForm(false); setForm(EMPTY); setSelectedClient(null); toast.success('Job Order created!'); },
    onError: e=>toast.error(e.response?.data?.message||'Error'),
  });
  const updateMut = useMutation({
    mutationFn: ({id,data})=>api.put('/job-orders/'+id, data),
    onSuccess:()=>{ qc.invalidateQueries(['job-orders']); setEditing(null); setSelectedClient(null); toast.success('Updated!'); },
  });
  const deleteMut = useMutation({
    mutationFn: id=>api.delete('/job-orders/'+id),
    onSuccess:()=>{ qc.invalidateQueries(['job-orders']); toast.success('Deleted'); },
  });

  const jos     = joData?.data || [];
  const clients = clientData?.data || [];

  const handleClientChange = (clientId) => {
    set('client_id', clientId);
    const client = clients.find(c=>String(c.id)===String(clientId));
    setSelectedClient(client||null);
  };

  const handleSave = () => {
    if (!form.client_id) return toast.error('Please select a client');
    editing ? updateMut.mutate({id:editing.id, data:form}) : createMut.mutate(form);
  };

  const openEdit = jo => {
    setEditing(jo);
    setForm({ client_id:jo.client_id, contract_no:jo.contract_no||'', scope_of_work:jo.scope_of_work||'', business_days:jo.business_days||'', deadline:jo.deadline||'', assigned_to:jo.assigned_to||'', technical_manager:jo.technical_manager||'', project_manager:jo.project_manager||'', status:jo.status, notes:jo.notes||'' });
    setSelectedClient(jo.client||null);
    setShowForm(false);
  };

  return (
    <div>
      <div className="g4" style={{marginBottom:20}}>
        <div className="card-sm"><div className="stat-label">Total JOs</div><div className="stat-val">{jos.length}</div></div>
        <div className="card-sm"><div className="stat-label">In Progress</div><div className="stat-val" style={{color:'var(--info)'}}>{jos.filter(j=>j.status==='In Progress').length}</div></div>
        <div className="card-sm"><div className="stat-label">Completed</div><div className="stat-val" style={{color:'var(--green)'}}>{jos.filter(j=>j.status==='Completed').length}</div></div>
        <div className="card-sm"><div className="stat-label">Pending Review</div><div className="stat-val" style={{color:'var(--warn)'}}>{jos.filter(j=>j.status==='Review').length}</div></div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap',alignItems:'center'}}>
        <div className="tab-bar">
          {['All',...STATUSES].map(s=><button key={s} className={"tab-btn"+(statusFilter===s?" active":"")} onClick={()=>setStatusFilter(s)}>{s}</button>)}
        </div>
        <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>{setShowForm(v=>!v);setEditing(null);setForm(EMPTY);setSelectedClient(null);}}>+ New Job Order</button>
      </div>

      {(showForm||editing) && (
        <div className="card" style={{marginBottom:16}}>
          <div className="form-section-title">{editing?'Edit Job Order - JO #'+editing.jo_number:'New Job Order'}</div>

          <div className="form-grid" style={{marginBottom:16}}>
            <div className="form-group"><label className="form-label">Client *</label>
              <select className="form-select" value={form.client_id} onChange={e=>handleClientChange(e.target.value)}>
                <option value="">Select client...</option>
                {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Contract No</label>
              <input className="form-input" value={form.contract_no} onChange={e=>set('contract_no',e.target.value)} placeholder="CONTRACT-001"/></div>
            <div className="form-group"><label className="form-label">Business Days</label>
              <input className="form-input" type="number" value={form.business_days} onChange={e=>set('business_days',e.target.value)} placeholder="e.g. 30"/></div>
            <div className="form-group"><label className="form-label">Deadline</label>
              <input className="form-input" type="date" value={form.deadline} onChange={e=>set('deadline',e.target.value)}/></div>
            <div className="form-group"><label className="form-label">Assigned To</label>
              <input className="form-input" value={form.assigned_to} onChange={e=>set('assigned_to',e.target.value)} placeholder="Developer/Team name"/></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-select" value={form.status} onChange={e=>set('status',e.target.value)}>
                {STATUSES.map(s=><option key={s}>{s}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Technical Manager</label>
              <input className="form-input" value={form.technical_manager} onChange={e=>set('technical_manager',e.target.value)} placeholder="Technical Manager name"/></div>
            <div className="form-group"><label className="form-label">Project Manager</label>
              <input className="form-input" value={form.project_manager} onChange={e=>set('project_manager',e.target.value)} placeholder="Project Manager name"/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Scope of Work</label>
              <textarea className="form-textarea" style={{minHeight:120}} value={form.scope_of_work} onChange={e=>set('scope_of_work',e.target.value)} placeholder="Describe the full scope of work..."/></div>
            <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Notes</label>
              <textarea className="form-textarea" value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Additional notes..."/></div>
          </div>

          {selectedClient && (
            <div style={{background:'var(--green-light)',border:'1px solid var(--green)',borderRadius:10,padding:16,marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,color:'var(--green)',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Client Information</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Name</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.name}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Company</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.company||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Phone</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.phone||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Email</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.email||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Civil ID</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.civil_id||'--'}</div></div>
                <div><div style={{fontSize:11,color:'var(--muted)',marginBottom:2}}>Address</div><div style={{fontWeight:600,fontSize:13}}>{selectedClient.address||'--'}</div></div>
              </div>
            </div>
          )}

          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-primary" onClick={handleSave}>Save Job Order</button>
            <button className="btn btn-outline" onClick={()=>{setShowForm(false);setEditing(null);setSelectedClient(null);}}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card">
        {isLoading?<div className="loading-center"><div className="spinner"/></div>:(
          <table className="tbl">
            <thead>
              <tr><th>JO #</th><th>Client</th><th>Contract No</th><th>Scope</th><th>Days</th><th>Deadline</th><th>Assigned To</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {jos.length===0&&<tr><td colSpan={9} className="empty-state">No job orders found</td></tr>}
              {jos.map(jo=>(
                <tr key={jo.id}>
                  <td style={{fontFamily:'monospace',fontWeight:700,color:'var(--green)'}}>JO #{jo.jo_number}</td>
                  <td><div style={{fontWeight:600}}>{jo.client?.name||'--'}</div><div style={{fontSize:11,color:'var(--muted)'}}>{jo.client?.company||''}</div></td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{jo.contract_no||'--'}</td>
                  <td style={{maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'var(--muted)',fontSize:12}}>{jo.scope_of_work||'--'}</td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{jo.business_days?jo.business_days+'d':'--'}</td>
                  <td style={{color:'var(--muted)',fontSize:12}}>{jo.deadline||'--'}</td>
                  <td style={{fontSize:12}}>{jo.assigned_to||'--'}</td>
                  <td><span className={"badge "+(STATUS_BADGE[jo.status]||'badge-muted')}>{jo.status}</span></td>
                  <td>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <button className="btn btn-outline btn-sm" onClick={()=>openEdit(jo)}>Edit</button>
                      <button className="btn btn-outline btn-sm" style={{color:'var(--info)',borderColor:'var(--info)'}} onClick={()=>printJobOrder(jo)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        Print
                      </button>
                      <select className="form-select" style={{width:110,padding:'4px 8px',fontSize:11}} value={jo.status}
                        onChange={e=>updateMut.mutate({id:jo.id,data:{status:e.target.value}})}>
                        {STATUSES.map(s=><option key={s}>{s}</option>)}
                      </select>
                      <button className="btn btn-danger btn-sm" onClick={()=>{if(window.confirm('Delete?'))deleteMut.mutate(jo.id);}}>X</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
